import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { OfferCategory } from '@cds/betting-offer';
import { OfferMapping } from '@cds/query-objects';
import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { FavouritesService } from '@frontend/sports/favourites/core/feature';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { sortBy } from 'lodash-es';
import { skip } from 'rxjs';

import { EpcotConfigService } from '../common/epcot-config.service';
import { FixtureList } from '../event-list-shared/sport/competitions/competition.models';
import { LiveFavouriteFixturesService } from '../favourites/live-favourites-fixtures.service';
import { LiveFavouritesModel } from './live-event-list.service';

export interface GridFixtureList {
    grid: ObservableGrid;
    favouriteFixture: FixtureList;
    liveFixturesCount: number;
    order: number;
}

@HooksWireup()
@Component({
    selector: 'ms-live-favourites-overview',
    templateUrl: './live-favourites-overview.component.html',
    providers: [ObservableGridFactory],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LiveFavouritesOverviewComponent extends OnRouteResolve<LiveFavouritesModel> {
    gridFixtureList: GridFixtureList[] = [];
    sports: CountItem[] = [];
    tracking = 'LivePage/Favourites';
    isEpcotEnabled: boolean;

    @HostBinding('class') className = 'live-favourites-overview';

    constructor(
        public sitecore: Sitecore,
        private liveFavouriteFixturesService: LiveFavouriteFixturesService,
        favouritesService: FavouritesService,
        private gridFactory: ObservableGridFactory,
        private redirectHelper: RedirectHelperService,
        route: ActivatedRoute,
        private changeDetector: ChangeDetectorRef,
        private epcotConfigService: EpcotConfigService,
    ) {
        super(route);
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
        favouritesService.change.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
            this.liveFavouriteFixturesService.refresh(OfferMapping.Filtered, OfferCategory.Gridable);
        });

        this.liveFavouriteFixturesService.change.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
            this.refreshFavourites();
        });
    }

    onRouteResolved(model: LiveFavouritesModel | undefined): void {
        if (!model) {
            return;
        }

        this.sports = model.sports;
        this.refreshFavourites();
    }

    goTo(sport: SportModel): void {
        this.redirectHelper.goToLiveSport({ sport, isEsport: sport.isEsport });
    }

    private refreshFavourites(): void {
        if (this.liveFavouriteFixturesService.liveFixtures.length) {
            this.setFixtures();
            this.changeDetector.markForCheck();
        } else {
            this.redirectHelper.goToLive();
        }
    }

    private setFixtures(): void {
        this.destroyGrids();

        const fixtureLists = this.liveFavouriteFixturesService.getFixtureList();
        if (!fixtureLists?.length) {
            return;
        }

        this.gridFixtureList = sortBy(fixtureLists.map(this.mapToGridFixtureList), (gridFixtureList) => gridFixtureList.order);
    }

    private destroyGrids(): void {
        this.gridFixtureList.forEach((g) => g.grid.destroy());
        this.gridFixtureList = [];
    }

    private mapToGridFixtureList = (fixtureList: FixtureList): GridFixtureList => {
        const sportId = fixtureList.fixtures[0].sport.id;
        const sportItem = this.sports.find((sport) => sport.id === sportId);

        return {
            grid: this.gridFactory.fromResponse(`${sportId}/live-favourites`, fixtureList.fixtures),
            favouriteFixture: fixtureList,
            liveFixturesCount: sportItem ? sportItem.counts.live : fixtureList.fixtures.length,
            order: sportItem?.order ?? Infinity,
        };
    };
}
